import { Marked } from 'marked'
import { markedHighlight } from 'marked-highlight'
import hljs from 'highlight.js'
import DOMPurify from 'dompurify'
import { useBridgeStore } from '../stores/bridge.js'
import { deriveSpaBase } from '../utils/spaBase.js'

const renderer = {
  link({ href, title, tokens }) {
    const text = this.parser.parseInline(tokens)
    const titleAttr = title ? ` title="${title}"` : ''
    return `<a href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`
  },
}

function escHtml(value) {
  return String(value).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ))
}

// Build the Finder URL for a single clientId — the exact destination rdrive_finder_url
// produces for {issue,process}-client-ids=<code>. Reads projectCode/apiUrl from the
// bridge store at render time, so it tracks host config changes.
function buildFinderUrl(kind, code) {
  const bridge = useBridgeStore()
  const base = deriveSpaBase(bridge.apiUrl)
  if (!base || !bridge.projectCode) return null
  const path = kind === 'process' ? 'processes' : 'issues'
  const key = kind === 'process' ? 'process-client-ids' : 'issue-client-ids'
  return `${base}/${encodeURIComponent(bridge.projectCode)}/${path}`
    + `?${key}=${encodeURIComponent(code)}`
}

// Build the deep link for a single task — the exact destination rdrive_deep_link
// produces for viewType="task-viewer": /{projectCode}/task-viewer/{scheduleId}
// opens the schedule, #view/task{taskId} highlights the task. The hash id is left
// raw for parity with the backend's DeepLinkTools.BuildHashFragment.
function buildTaskViewerUrl(scheduleId, taskId) {
  const bridge = useBridgeStore()
  const base = deriveSpaBase(bridge.apiUrl)
  if (!base || !bridge.projectCode || !scheduleId || !taskId) return null
  return `${base}/${encodeURIComponent(bridge.projectCode)}/task-viewer/${encodeURIComponent(scheduleId)}`
    + `#view/task${taskId}`
}

// Inline token the agent emits for a per-item link: [[rdrive:issue:DEF-012]],
// [[rdrive:process:PRM-0007]], or [[rdrive:task:<scheduleId>:<taskId>]] (each with an
// optional |label suffix). Rendered as a clickable chip whose URL is built from the
// host's projectCode/apiUrl — issue/process open the Finder, task opens the task
// viewer. Unknown kinds and malformed task tokens (wrong id count) never reach the
// renderer; missing config falls back to plain text rather than a dead button.
//
// Code-span tolerance: the system prompt shows the token format in backticks, and
// the model sometimes copies that markup literally (`[[rdrive:issue:DEF-012]]`). A
// code span holding exactly one token, and nothing else, is treated as the token —
// the user asked for a link, not for the raw syntax. Any other code span (extra
// text, an unbalanced backtick) and every fenced block stay literal, so docs that
// deliberately quote the syntax still render as code. Inline extensions never run
// inside fences, and they run before marked's own codespan tokenizer, which is what
// lets this match win at the opening backtick.
const rdriveItemExtension = {
  name: 'rdriveItem',
  level: 'inline',
  start(src) {
    const i = src.indexOf('[[rdrive:')
    if (i < 0) return undefined
    // Start at the backtick when the token is wrapped, so the text tokenizer stops
    // there and the tokenizer below gets to see the whole `[[...]]` form.
    return i > 0 && src[i - 1] === '`' ? i - 1 : i
  },
  tokenizer(src) {
    // Group 1 is the optional wrapping backtick; the \1 backreference demands the
    // matching closer, so an unbalanced backtick falls through to plain codespan/text
    // handling. Task tokens carry two ids (scheduleId:taskId). Excluding backslashes
    // from the id charclasses makes an un-de-escaped \| (agent escaping outside a
    // table) fail the match and degrade to plain text instead of a corrupt URL.
    const t = /^(`?)\[\[rdrive:task:([^:\]|\\]+):([^:\]|\\]+)(?:\|([^\]]+))?\]\]\1/i.exec(src)
    if (t) {
      return {
        type: 'rdriveItem',
        raw: t[0],
        kind: 'task',
        scheduleId: t[2].trim(),
        code: t[3].trim(),
        label: (t[4] || t[3]).trim(),
      }
    }
    const m = /^(`?)\[\[rdrive:(issue|process):([^\]|]+?)(?:\|([^\]]+))?\]\]\1/i.exec(src)
    if (!m) return undefined
    return {
      type: 'rdriveItem',
      raw: m[0],
      kind: m[2].toLowerCase(),
      code: m[3].trim(),
      label: (m[4] || m[3]).trim(),
    }
  },
  renderer(token) {
    const url = token.kind === 'task'
      ? buildTaskViewerUrl(token.scheduleId, token.code)
      : buildFinderUrl(token.kind, token.code)
    if (!url) return escHtml(token.label)
    const schedAttr = token.kind === 'task'
      ? ` data-schedule-id="${escHtml(token.scheduleId)}"`
      : ''
    return `<a class="rdrive-item-chip" data-kind="${token.kind}" data-code="${escHtml(token.code)}"${schedAttr}`
      + ` href="${escHtml(url)}" target="_blank" rel="noopener noreferrer">${escHtml(token.label)}</a>`
  },
}

const marked = new Marked(
  markedHighlight({
    langPrefix: 'hljs language-',
    highlight(code, lang) {
      if (lang && hljs.getLanguage(lang)) {
        return hljs.highlight(code, { language: lang }).value
      }
      return hljs.highlightAuto(code).value
    },
  }),
  { breaks: true, gfm: true, renderer },
)

marked.use({ extensions: [rdriveItemExtension] })

export function useMarkdown() {
  function render(text) {
    if (!text) return ''
    const html = marked.parse(text)
    return DOMPurify.sanitize(html, { ADD_ATTR: ['target', 'data-kind', 'data-code', 'data-schedule-id'] })
  }

  return { render }
}
